import type { DependencyList } from 'react'
import type { FrameCallback, MeasureCallback } from '@/lib/motion/engine'

import { useEffect } from 'react'
import { clamp } from '@/lib/motion/math'
import { engine } from '@/lib/motion/engine'

export interface SectionMetrics {
    top: number
    height: number
    frame: number
}

export interface RelativeRect {
    left: number
    top: number
    width: number
    height: number
    cx: number
    cy: number
}

export const emptyMetrics = (): SectionMetrics => ({ top: 0, height: 1, frame: 1 })

export const measureSection = (element: HTMLElement, frame?: HTMLElement | null): SectionMetrics => {
    const rect = element.getBoundingClientRect()

    return {
        top: rect.top + window.scrollY,
        height: rect.height,
        frame: frame ? frame.offsetHeight : window.innerHeight
    }
}

/** Rect of `element` measured relative to `frame` (a pinned, full-viewport container). */
export const relativeRect = (element: HTMLElement, frame: HTMLElement): RelativeRect => {
    const a = element.getBoundingClientRect()
    const b = frame.getBoundingClientRect()
    const left = a.left - b.left
    const top = a.top - b.top

    return {
        left,
        top,
        width: a.width,
        height: a.height,
        cx: left + a.width / 2,
        cy: top + a.height / 2
    }
}

/** Rect of `element` in document coordinates. */
export const documentRect = (element: HTMLElement): RelativeRect => {
    const a = element.getBoundingClientRect()
    const left = a.left + window.scrollX
    const top = a.top + window.scrollY

    return {
        left,
        top,
        width: a.width,
        height: a.height,
        cx: left + a.width / 2,
        cy: top + a.height / 2
    }
}

/** Raw (unclamped) progress through a pinned section: 0 at pin start, 1 at pin end. */
export const pinRaw = (metrics: SectionMetrics, scroll: number): number => {
    return (scroll - metrics.top) / Math.max(1, metrics.height - metrics.frame)
}

export const pinProgress = (metrics: SectionMetrics, scroll: number): number => {
    return clamp(pinRaw(metrics, scroll))
}

/** 0 when the section top sits at the viewport bottom, 1 when it reaches the viewport top. */
export const enterProgress = (metrics: SectionMetrics, scroll: number, vh: number): number => {
    return clamp((scroll - metrics.top + vh) / Math.max(1, vh))
}

/** 0 when the section bottom sits at the viewport bottom, 1 when it reaches the viewport top. */
export const exitProgress = (metrics: SectionMetrics, scroll: number, vh: number): number => {
    return clamp((scroll - (metrics.top + metrics.height - vh)) / Math.max(1, vh))
}

export const isNear = (metrics: SectionMetrics, scroll: number, vh: number, margin = 0.25): boolean => {
    return scroll + vh * (1 + margin) > metrics.top && scroll - vh * margin < metrics.top + metrics.height
}

interface EngineSetup {
    measure?: MeasureCallback
    frame?: FrameCallback
    priority?: number
    cleanup?: () => void
}

/** Registers a measure + frame pair with the shared animation engine. */
export const useEngine = (setup: () => EngineSetup | void, deps: DependencyList = []): void => {
    useEffect(() => {
        const result = setup()

        if (!result) return

        const offs: Array<() => void> = []

        if (result.measure) offs.push(engine.onMeasure(result.measure))
        if (result.frame) offs.push(engine.subscribe(result.frame, result.priority ?? 0))

        return () => {
            offs.forEach(off => off())
            result.cleanup?.()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps)
}
